import React, { useCallback, useEffect, useMemo, useState } from "react";
import styled from "styled-components";
import { useLocation, useNavigate } from "react-router-dom";
import { api } from "../../helpers/utils";
import { useGoogleAuth } from "../../hooks/useGoogleAuth";
import Cropper from "react-easy-crop";

const DEFAULT_ADMIN_EMAIL = "destinycoreoperations@gmail.com";
const FALLBACK_TITLE = "Plan meet-ups";
const FALLBACK_DESC = "Invite your friends to join Pepchat and vibe together.";
const FALLBACK_CTA = "Explore";
const MAX_PREVIEW_HEIGHT = 160;
const PREVIEW_ASPECT = 1170 / 441;
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous");
    image.src = url;
  });

const getCroppedBlob = async (imageSrc, crop, mimeType = "image/jpeg") => {
  if (!crop || !imageSrc) return null;
  const safeCrop = {
    width: Math.round(crop.width),
    height: Math.round(crop.height),
    x: Math.round(crop.x),
    y: Math.round(crop.y),
  };

  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  canvas.width = safeCrop.width;
  canvas.height = safeCrop.height;

  ctx.drawImage(
    image,
    safeCrop.x,
    safeCrop.y,
    safeCrop.width,
    safeCrop.height,
    0,
    0,
    safeCrop.width,
    safeCrop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Unable to process cropped image."));
          return;
        }
        resolve(blob);
      },
      mimeType,
      0.95
    );
  });
};

export default function AddPromotion() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [loading, setLoading] = useState(false);
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [msg, setMsg] = useState("");
  const [prefillLoading, setPrefillLoading] = useState(false);
  const [prefillError, setPrefillError] = useState("");
  const [promotionId, setPromotionId] = useState("");
  const [queryGmail, setQueryGmail] = useState("");
  const [persistedImage, setPersistedImage] = useState("");
  const [isAdminUser, setIsAdminUser] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);

  // Form Fields
  const [title, setTitle] = useState("");
  const [desc, setDesc] = useState("");
  const [link, setLink] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useGoogleAuth();

  const onFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
  }, [preview]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const gmailParam = params.get("gmail") || params.get("email");
    const promoParam = params.get("promotionId") || params.get("adId");

       const isAdminFlag = localStorage.getItem("isAdmin") === "true";
    setIsAdminUser(isAdminFlag);


    setQueryGmail(gmailParam || "");
    setPromotionId(promoParam || "");

      if (!isAdminFlag && !gmailParam) {
      navigate("/ads");
    }

  }, [location.search]);


  const resolvedEmail = useMemo(() => {
    if (queryGmail) return queryGmail;
    if (user?.email) return user.email;
    if (!promotionId) return DEFAULT_ADMIN_EMAIL;
    return null;
  }, [queryGmail, user?.email, promotionId]);

  useEffect(() => {
    if (!promotionId || !resolvedEmail) return;
    const fetchPromotion = async () => {
      setPrefillLoading(true);
      setPrefillError("");
      setMsg("");
      try {
        const res = await api.post("/my-promotions", { email: resolvedEmail });
        const list = res.data?.data || [];
        console.log("Fetched promotions for prefill:", list);
        const promo = list.find((item) => item._id === promotionId);
        if (promo) {
          setTitle(promo.title || "");
          setDesc(promo.desc || "");
          setLink(promo.link || "");
          setPreview(promo.image || "");
          setPersistedImage(promo.image || "");
          setFile(null);
        } else {
          setPrefillError("Promotion not found for this account.");
        }
      } catch (error) {
        setPrefillError("Unable to load promotion details.");
      } finally {
        setPrefillLoading(false);
      }
    };

    fetchPromotion();
  }, [promotionId, resolvedEmail]);

  const isEditing = Boolean(promotionId);
  const shouldSkipPayment = isEditing || isAdminUser;
  const PAYMENT_AMOUNT_INR = 100;

  const validateForm = () => {
    if (!title || !desc || !link) {
      setMsg("Title, description & link are required");
      return false;
    }
    if (!resolvedEmail) {
      setMsg("A valid email is required to continue.");
      return false;
    }
    return true;
  };

  const uploadImageIfNeeded = async () => {
    let imageUrl = persistedImage;

    if (file) {
      let imageToUpload = file;

      if (preview && croppedAreaPixels) {
        try {
          const croppedBlob = await getCroppedBlob(preview, croppedAreaPixels, file.type || "image/jpeg");
          imageToUpload = new File([croppedBlob], file.name, {
            type: croppedBlob.type || file.type || "image/jpeg",
          });
        } catch (error) {
          console.error("Unable to crop image before upload", error);
          setMsg("Couldn't apply crop, uploading the original image.");
        }
      }

      const form = new FormData();
      form.append("file", imageToUpload);
      form.append("type", "image");

      const uploadRes = await api.post("/upload-media", form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (uploadRes.data.status === 100 && uploadRes.data.url) {
        imageUrl = uploadRes.data.url;
        setPersistedImage(uploadRes.data.url);
      } else {
        throw new Error("Image upload failed. Please try again.");
      }
    }

    return imageUrl;
  };

const uploadPromotion = async () => {
  if (!validateForm()) return;
  setLoading(true);
  setMsg("");

  try {
    const imageUrl = await uploadImageIfNeeded();

    const body = {
      title,
      desc,
      link,
      image: imageUrl,
      email: resolvedEmail || "destinycoreoperations@gmail.com",
      userId: user?.uid,
    };

    if (promotionId) {
      body.promotionId = promotionId;
    }

    const res = await api.post("/add-promotion", body);

    if (res.data.status === 100) {
      setMsg("Promotion uploaded successfully");
      if(!isAdminUser) navigate("/ads", { state: { refreshAds: true } });
    } else {
      setMsg("Failed to add promotion");
    }

  } catch (err) {
    console.log(err);
    setMsg(err.message || "Upload failed");
  } finally {
    setLoading(false);
  }
};

  const handleCropComplete = useCallback((_, croppedPixels) => {
    setCroppedAreaPixels(croppedPixels);
  }, []);
  const handleZoomChange = (event) => {
    setZoom(Number(event.target.value));
  };

const handlePayAndUpload = async () => {
  if (!validateForm()) return;

  if (typeof window === "undefined" || !window.Razorpay) {
    setMsg("Payment gateway unavailable. Refresh and try again.");
    return;
  }

  setMsg("");
  setLoading(true);

  let imageUrl = "";
  try {
    imageUrl = await uploadImageIfNeeded();
  } catch (error) {
    setLoading(false);
    setMsg(error.message || "Image upload failed. Please try again.");
    return;
  }

  try {
    // 🔥 CREATE ORDER FROM BACKEND
    const res = await api.post("/create-ad-promotion-order", {
    
      email: resolvedEmail,
      title,
      desc: desc,
      link,
      image: imageUrl,
      promotionId,
      amount: PAYMENT_AMOUNT_INR, // rupees
    });

    const { orderId, razorpayKeyId } = res.data.data;

    setLoading(false);
    setPaymentProcessing(true);

    const options = {
      key: razorpayKeyId,
      order_id: orderId, // ✅ REQUIRED
      currency: "INR",
      name: "Pepchat Ads",
      desc: "1-week promotion (500 guaranteed views)",
      prefill: { email: resolvedEmail },
      theme: { color: "#000000" },

      handler: function () {
        setPaymentProcessing(false);
        setMsg("Payment successful! We will publish your promotion shortly.");
        navigate("/ads", { state: { refreshAds: true } });
      },


      modal: {
        ondismiss: () => {
          setPaymentProcessing(false);
          setMsg("Payment cancelled.");
        },
      },
    };

    const rzp = new window.Razorpay(options);

    rzp.on("payment.failed", () => {
      setPaymentProcessing(false);
      setMsg("Payment failed. Please try again.");
    });

    rzp.open();
  } catch (err) {
    setLoading(false);
    setPaymentProcessing(false);
    setMsg("Unable to initiate payment. Please try again.");
  }
};



  return (
    <Page>
      <Header>
        <Heading>Upload Promotion</Heading>
        <SubHeading>Share contextual promotion cards in a calm workflow</SubHeading>
      </Header>

    

      <Content>
        <PreviewPanel>
          <PanelLabel>Live preview</PanelLabel>
          <PanelHint>Preview window is capped at {MAX_PREVIEW_HEIGHT}px height.</PanelHint>
          <BannerPreviewCard>
            <BannerMedia>
              {preview ? (
                file ? (
                  <CropSurface>
                    <Cropper
                      image={preview}
                      crop={crop}
                      zoom={zoom}
                      aspect={PREVIEW_ASPECT}
                      cropShape="rect"
                      showGrid={false}
                      onCropChange={setCrop}
                      onCropComplete={handleCropComplete}
                      onZoomChange={setZoom}
                    />
                  </CropSurface>
                ) : (
                  <BannerImage src={preview} alt="Promotion preview" />
                )
              ) : (
                <BannerPlaceholder>
                  Upload an image to preview (max height {MAX_PREVIEW_HEIGHT}px)
                </BannerPlaceholder>
              )}
              <AdBadge>Ad</AdBadge>
              {preview && <HeightTag>Max height {MAX_PREVIEW_HEIGHT}px</HeightTag>}
            </BannerMedia>

            {preview && file && (
              <CropControls>
                <ControlRow>
                  <ControlLabel>Adjust zoom</ControlLabel>
                  <ControlValue>{zoom.toFixed(1)}x</ControlValue>
                </ControlRow>
                <ZoomSlider
                  type="range"
                  min={MIN_ZOOM}
                  max={MAX_ZOOM}
                  step="0.1"
                  value={zoom}
                  onChange={handleZoomChange}
                />
                <MaxHeightHint>
                  Preview area is capped at {MAX_PREVIEW_HEIGHT}px height. Drag to reposition and zoom—the cropped region is what gets uploaded.
                </MaxHeightHint>
              </CropControls>
            )}

            <BannerContent>
              <TextGroup>
                <BannerTitle>{title || FALLBACK_TITLE}</BannerTitle>
                <BannerDesc>{desc || FALLBACK_DESC}</BannerDesc>
              </TextGroup>
              <BannerCTA type="button">{link ? "Open link" : FALLBACK_CTA}</BannerCTA>
            </BannerContent>
          </BannerPreviewCard>
        </PreviewPanel>

        <FormCard>
          <InputGroup>
            <Label>Upload image</Label>
            <FileInput type="file" accept="image/*" onChange={onFileChange} />
          </InputGroup>

          {prefillLoading && <PrefillHint>Loading promotion details…</PrefillHint>}

          <InputGroup>
            <Label>Title</Label>
            <TextInput placeholder="Example: Tiramisu Workshop" value={title} onChange={(e) => setTitle(e.target.value)} />
          </InputGroup>

          <InputGroup>
            <Label>Description</Label>
            <TextInput
              placeholder="Short description of the promotion"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
            />
          </InputGroup>

          <InputGroup>
            <Label>Link (Deep link / external URL)</Label>
            <TextInput
              placeholder="destiny://event/123  or  https://destinyapp.in/event/123"
              value={link}
              onChange={(e) => setLink(e.target.value)}
            />
          </InputGroup>

          <Button
            onClick={shouldSkipPayment ? uploadPromotion : handlePayAndUpload}
            disabled={loading || prefillLoading || paymentProcessing}
          >
            {shouldSkipPayment
              ? loading
                ? "Saving…"
                : "Save Promotion"
              : paymentProcessing
              ? "Processing Payment…"
              : loading
              ? "Uploading…"
              : "Pay ₹100 & Publish"}
          </Button>

          {(prefillError || msg) && <Msg>{prefillError || msg}</Msg>}
        </FormCard>
      </Content>
    </Page>
  );
}

/* Styles */

const Page = styled.div`
  min-height: 100vh;
  background: #000000;
  color: #ffffff;
  padding: 50px min(8vw, 60px) 100px;
`;

const Header = styled.div`
  margin-bottom: 30px;
`;

const Heading = styled.h1`
  font-size: 38px;
  margin: 0;
  color: #ffffff;
`;

const SubHeading = styled.p`
  margin-top: 12px;
  color: #bdbdbd;
  font-size: 16px;
`;

const PromoInfo = styled.div`
  background: #050505;
  border: 1px solid #303030;
  padding: 24px;
  margin-bottom: 32px;
  display: flex;
  gap: 20px;
  align-items: center;
  flex-wrap: wrap;
`;

const PromoText = styled.div`
  flex: 1;
  min-width: 240px;
`;

const PromoEyebrow = styled.p`
  text-transform: uppercase;
  letter-spacing: 0;
  font-size: 11px;
  color: #ffffff;
  margin: 0 0 6px;
`;

const PromoTitle = styled.h3`
  margin: 0 0 10px;
  color: #ffffff;
  font-size: 20px;
`;

const PromoDesc = styled.p`
  margin: 0;
  color: #bdbdbd;
  line-height: 1.6;
  font-size: 14px;
`;

const DownloadButton = styled.a`
  padding: 14px 22px;
  background: #ffffff;
  border: 1px solid #ffffff;
  color: #000000;
  font-weight: 800;
  text-decoration: none;
  white-space: nowrap;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #000000;
    color: #ffffff;
    transform: translateY(-2px);
  }
`;

const Content = styled.div`
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewPanel = styled.div`
  // background: #050505;
  border-radius: 22px;
  padding: 5px;
  // border: 1px solid rgba(255, 255, 255, 0.05);
  // box-shadow: 0 30px 50px rgba(5, 5, 12, 0.6);
`;

const PanelLabel = styled.p`
  margin: 0 0 14px;
  font-size: 14px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #ffffff;
`;

const PanelHint = styled.p`
  margin: -6px 0 16px;
  font-size: 12px;
  color: #8c8c8c;
`;

const BannerPreviewCard = styled.div`
  width: 100%;
  overflow: hidden;
  border: 1px solid #303030;
  background: #050505;
  display: flex;
  flex-direction: column;
`;

const BannerMedia = styled.div`
  position: relative;
  width: 100%;
  height: ${MAX_PREVIEW_HEIGHT}px;
  max-height: ${MAX_PREVIEW_HEIGHT}px;
  overflow: hidden;
  background: #000000;
`;

const CropSurface = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  background: #000;
  .reactEasyCrop_Container {
    background: transparent;
  }
`;

const BannerImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BannerPlaceholder = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 0 30px;
  font-size: 14px;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.85);
  height: 100%;
  background: #000000;
`;

const BannerContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  padding: clamp(20px, 4vw, 28px);

  @media (max-width: 620px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 18px;
  }
`;

const CropControls = styled.div`
  padding: 14px clamp(18px, 4vw, 28px) 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ControlRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #bdbdbd;
`;

const ControlLabel = styled.span`
  letter-spacing: 0;
  text-transform: uppercase;
`;

const ControlValue = styled.span`
  font-weight: 600;
  color: #ffffff;
`;

const ZoomSlider = styled.input`
  -webkit-appearance: none;
  width: 100%;
  height: 4px;
  background: #303030;
  outline: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 16px;
    height: 16px;
    background: #ffffff;
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border: none;
    background: #ffffff;
  }
`;

const MaxHeightHint = styled.p`
  margin: 2px 0 0;
  font-size: 12px;
  color: #8c8c8c;
  line-height: 1.4;
`;

const TextGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const BannerTitle = styled.h3`
  margin: 0;
  font-size: 24px;
  color: #ffffff;
`;

const BannerDesc = styled.p`
  margin: 0;
  font-size: 16px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.92);
`;

const BannerCTA = styled.button`
  padding: clamp(8px, 1.3vw, 12px) clamp(20px, 3vw, 32px);
  background: #ffffff;
  border: 1px solid #ffffff;
  font-weight: 800;
  font-size: 16px;
  color: #000000;
  letter-spacing: 0;
  text-transform: capitalize;
  cursor: pointer;
`;

const AdBadge = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  padding: 4px 12px;
  font-size: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: #f7f7ff;
  background: rgba(6, 6, 12, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.18);
`;

const HeightTag = styled.span`
  position: absolute;
  left: 12px;
  bottom: 12px;
  padding: 3px 10px;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0;
  color: rgba(255, 255, 255, 0.85);
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const FormCard = styled.div`
  background: #050505;
  padding: 36px 28px 90px;
  border: 1px solid #303030;
  display: flex;
  flex-direction: column;
  gap: 22px;
  position: relative;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Label = styled.label`
  font-size: 14px;
  color: #bdbdbd;
`;

const FileInput = styled.input`
  width: 100%;
  border: 1px dashed #555555;
  padding: 14px;
  background: #000000;
  color: #ffffff;
`;

/* Inputs */
const TextInput = styled.input`
  width: 100%;
  background: #000000;
  border: 1px solid #303030;
  padding: 12px 16px;
  color: #ffffff;
  transition: border 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #ffffff;
    outline: none;
  }
`;

const Button = styled.button`
  width: 100%;
  padding: 16px;
  background: #ffffff;
  border: 1px solid #ffffff;
  color: #000000;
  font-weight: 800;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    background: #000000;
    color: #ffffff;
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    position: sticky;
    bottom: 15px;
    margin-top: 8px;
    box-shadow: 0 18px 35px rgba(0, 0, 0, 0.7);
  }
`;

const PrefillHint = styled.p`
  margin: 0;
  font-size: 14px;
  color: #8c8c8c;
`;

const Msg = styled.div`
  margin-top: 20px;
  opacity: 0.9;
  font-size: 14px;
  color: #ffffff;
`;
